import * as React from "react";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { intersection, isNil } from "ramda";

import { permissions } from "lib/biot/roles";
import { convertExhUserToCurrentUser } from "lib/extrahorizon/converters";
import { useExhAuth, useHospitalName } from "lib/extrahorizon/hooks";

import { UserInactivityHandler } from "../../inactivityHandler";
import { logout } from "../auth";
import { AuthContextType } from "../types";

export const AuthContext = React.createContext<AuthContextType>([
  { isPermitted: () => false },
  {
    currentUser: null,
    permissions: null,
    loading: true, // Start with loading true to match store initial state
  },
]);

export const checkPermissions = (permits: permissions, permissions?: Maybe<permissions>): boolean =>
  !isNil(permissions) ? intersection(permits, permissions).length > 0 : false;

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { currentUser: exhUser, error: exhError, loading: exhLoading, initializeAuth } = useExhAuth();
  const { showBoundary: onError } = useErrorBoundary();

  // Get converted user first
  const convertedUser = useMemo(() => convertExhUserToCurrentUser(exhUser), [exhUser]);

  // Get hospital name for the user's primary group
  const { hospitalName } = useHospitalName(convertedUser?.hospital?.id);

  // Initialize auth on mount to fetch current user if logged in
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Extract scopes from ExtraHorizon access token
  // For now, use permissions from converted user object
  const exhPermissions = useMemo<string[]>(() => {
    return (convertedUser?.role?.permissions || []) as string[];
  }, [convertedUser]);

  const permissions = useMemo<permissions>(() => {
    return exhPermissions as permissions;
  }, [exhPermissions]);

  const isPermitted = useCallback(
    (permits) => {
      // For ExtraHorizon users, check if user exists (don't require BioT cookie)
      if (!exhUser) {
        return false;
      }
      return checkPermissions(permits, permissions);
    },
    [exhUser, permissions]
  );

  useEffect(() => {
    if (exhError) {
      onError(exhError);
    }
  }, [exhError, onError]);

  const currentUser = useMemo(() => {
    if (!convertedUser || hospitalName === null) return convertedUser;
    return {
      ...convertedUser,
      hospital: convertedUser.hospital
        ? {
            ...convertedUser.hospital,
            name: hospitalName,
          }
        : undefined,
    };
  }, [convertedUser, hospitalName]);

  const context = useMemo<AuthContextType>(() => {
    return [
      { isPermitted },
      {
        currentUser,
        permissions: permissions,
        loading: exhLoading,
      },
    ];
  }, [currentUser, isPermitted, permissions, exhLoading]);

  const handleIdle = useCallback(async () => {
    // For ExtraHorizon, call the logout method which handles token cleanup
    logout();
  }, []);

  return (
    <AuthContext.Provider value={context}>
      {currentUser ? <UserInactivityHandler onIdle={handleIdle} /> : null}

      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => useContext(AuthContext);
